import React, { createContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { User } from '../types/cabinet.types';
import { getProfile, sendVerificationEmail } from '../api/cabinetApi';
import { logoutBackend } from '../api/authApi';
import { clearQueryCache } from '../hooks/useQuery';
import { setGa4UserId } from '../hooks/usePwaAnalytics';
import { getWeightHistory, getMyDietPlan } from '../api/dietApi';
import type { WeightHistory, DietPlan } from '../api/dietApi';

// Prefetch-кеш для миттєвого відображення графіку ваги та плану
// Заповнюється одразу після /users/me, ще до рендеру TherapyPage
export interface PrefetchedDietData {
  weightHistory: WeightHistory | null;
  dietPlan: DietPlan | null;
  loadedAt: number;
}
declare global {
  interface Window {
    __prefetchDiet?: PrefetchedDietData;
  }
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  isLoading: boolean;
  login: (token: string, refreshToken?: string) => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  verifyEmail: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchUser = useCallback(async () => {
    try {
      const profile = await getProfile();
      setUser(profile);
      setIsAuthenticated(true);
      // Встановлюємо GA4 user_id одразу після отримання профілю.
      // Це покриває всі сценарії: email/password логін, SMS OTP, відновлення сесії.
      // setGa4UserId має бути викликаний ДО інших GA4-подій — що і відбувається,
      // бо usePwaAnalytics залежить від userId і чекає його появи.
      setGa4UserId(profile.id);

      // ── Prefetch weight-history + my-plan в фоні ──────────────────────────
      // Запускаємо одразу після /users/me — поки React рендерить TherapyPage,
      // дані вже готові в window.__prefetchDiet. TherapyPage забере їх синхронно.
      // Prefetch тільки для Persona A / isWeightLoss (єдиний сегмент що показує графік).
      if (profile.personaType === 'A' || profile.isWeightLoss) {
        Promise.all([
          getWeightHistory().catch(() => null),
          getMyDietPlan().catch(() => null),
        ]).then(([weightHistory, dietPlan]) => {
          // Зберігаємо ТІЛЬКИ якщо weightHistory успішно завантажено.
          // Якщо null (мережева помилка, таймаут) — не пишемо нічого.
          // TherapyPage при відсутності prefetch зробить звичайний запит.
          if (weightHistory !== null) {
            window.__prefetchDiet = {
              weightHistory,
              dietPlan,
              loadedAt: Date.now(),
            };
          }
        });
      }
    } catch (error) {
      console.error('[AuthContext] Failed to fetch user profile:', error);
      // Якщо отримаємо 401 — interceptor спробує refresh,
      // якщо refresh теж не вийде — викине 'cabinet_auth_expired'
    } finally {
      setIsLoading(false);
    }
  }, []);

  const initAuth = useCallback(async () => {
    // 1. Пріоритет №1: Токен від WordPress сесії (initialToken)
    const initialToken = window.cabinetVars?.initialToken;
    if (initialToken) {
      sessionStorage.setItem('cab_token', initialToken);
      const initialRefresh = window.cabinetVars?.initialRefreshToken;
      if (initialRefresh) {
        localStorage.setItem('cab_refresh', initialRefresh);
      }
      const initialUser = window.cabinetVars?.initialUser;
      if (initialUser) {
        // emailVerified може бути відсутнім у кеші (ми не кешуємо його навмисно).
        // За замовчуванням true — щоб не блимав банер до завершення fetchUser().
        // Реальне значення прийде з fetchUser() у фоні.
        setUser({ ...initialUser, emailVerified: true }); // Cache може мати старе false; реальне значення прийде з fetchUser()
        setIsAuthenticated(true);
        setIsLoading(false);
        // Встановлюємо GA4 user_id одразу з кешованих даних — це важливо для
        // PWA standalone де кожен запуск = нова GA4-сесія. fetchUser() нижче
        // також викличе setGa4UserId — це ідемпотентна операція, без шкоди.
        setGa4UserId(initialUser.id);
        // Silently re-fetch profile in background to keep data fresh
        fetchUser();
        return;
      }
      
      await fetchUser();
      return;
    }

    // 2. Пріоритет №2: Токен з сесії (якщо WP сесії немає, наприклад юзер зайшов через React форму)
    const token = sessionStorage.getItem('cab_token');
    if (token) {
      if (typeof window !== 'undefined' && window.cabinetVars) {
        // wp_localize_script might convert false to "" or "0".
        const isLoggedInRaw = window.cabinetVars.isLoggedIn as any;
        const isLoggedIn = isLoggedInRaw === true || isLoggedInRaw === '1' || isLoggedInRaw === 'true';
        if (!isLoggedIn) {
          // WP session is gone, but we still have JWT. We must log out to prevent infinite reloads.
          console.warn('[AuthContext] WP session is missing. Clearing local JWT to sync state.');
          sessionStorage.removeItem('cab_token');
          localStorage.removeItem('cab_refresh');
          clearQueryCache();
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }
      }

      // JWT вже є в сесії — просто завантажуємо профіль
      const initialUser = window.cabinetVars?.initialUser;
      if (initialUser) {
        setUser(initialUser);
        setIsAuthenticated(true);
        setIsLoading(false);
        // Встановлюємо GA4 user_id з кешованих даних одразу (без очікування fetchUser)
        setGa4UserId(initialUser.id);
        fetchUser();
        return;
      }

      await fetchUser();
      return;
    }

    // Немає ні JWT, ні initial token — юзер не авторизований
    setIsAuthenticated(false);
    setIsLoading(false);
  }, [fetchUser]);


  useEffect(() => {
    initAuth();

    // Слухаємо подію закінчення токену від interceptor-а
    const handleAuthExpired = () => {
      logout();
    };
    window.addEventListener('cabinet_auth_expired', handleAuthExpired);
    return () => {
      window.removeEventListener('cabinet_auth_expired', handleAuthExpired);
    };
  }, []);

  const login = (token: string, refreshToken?: string) => {
    sessionStorage.setItem('cab_token', token);
    if (refreshToken) {
      localStorage.setItem('cab_refresh', refreshToken);
    }
    setIsAuthenticated(true);
    setIsLoading(true);
    fetchUser();
  };

  const logout = async () => {
    if (isAuthenticated) {
      try {
        await logoutBackend();
      } catch (error) {
        console.error(error);
      }
    }

    sessionStorage.removeItem('cab_token');
    localStorage.removeItem('cab_refresh');
    clearQueryCache();
    setIsAuthenticated(false);
    setUser(null);

    // Перенаправляємо на сторінку логіну
    if (typeof window !== 'undefined' && window.cabinetVars?.loginUrl) {
      const currentUrl = window.location.href.split('?')[0].replace(/\/$/, '');
      const loginUrl = window.cabinetVars.loginUrl.split('?')[0].replace(/\/$/, '');
      
      if (currentUrl !== loginUrl) {
        window.location.href = window.cabinetVars.loginUrl;
      }
    }
  };

  const refreshUser = async () => {
    if (isAuthenticated) {
      await fetchUser();
    }
  };

  const verifyEmail = async () => {
    await sendVerificationEmail();
    // Оновлюємо профіль щоб UI відобразив актуальний статус
    if (isAuthenticated) {
      await fetchUser();
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, isLoading, login, logout, refreshUser, verifyEmail }}>
      {children}
    </AuthContext.Provider>
  );
};

